import React, { useEffect, useMemo, useState } from "react";
import { useSearchData } from "./Rechercher";
import type { SearchHit, Article, TraductionEnFrancais } from "../models/Article";

/* ===== Utils ===== */
function normalize(s = "") {
  return s.toLowerCase().normalize("NFD").replace(/\p{Diacritic}/gu, "");
}
function escapeHtml(str = "") {
  const htmlEscapeMap: Record<string, string> = {
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#39;",
  };
  return str.replace(/[&<>"']/g, (m) => htmlEscapeMap[m] || m);
}

function highlight(text = "", query = "") {
  console.log("Highlighting text:", text, "with query:", query);
  const safe = escapeHtml(text || "");
  const q = normalize(query.trim());
  if (!q) return safe;
  const tokens = q.split(/\s+/).filter(Boolean).map(t => t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  if (!tokens.length) return safe;
  const re = new RegExp(tokens.join("|"), "gi");
  return safe.replace(re, (m) => `<span class="nyA-hl">${escapeHtml(m)}</span>`);
}

type ItemPart = { text: string; isItemTraite: boolean };
type RenderedUnite = {
  type: string;
  intro?: string;
  content?: string;
  contextualisationParts?: ItemPart[];
  reference?: string;
  traduction?: string;
  explication?: string;
};

function toStringArray(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.filter(Boolean).map((v) => String(v));
  }
  if (typeof value === "string" && value.trim()) {
    return [value];
  }
  return [];
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === "object" && !Array.isArray(value);
}

function joinTextParts(parts: unknown[]): string {
  return parts
    .filter((part) => typeof part === "string" && part.trim())
    .map((part) => String(part).trim())
    .join(" ")
    .trim();
}

function formatTraduction(value?: string | TraductionEnFrancais): string {
  if (!value) return "";
  if (typeof value === "string") return value;

  const mentions = toStringArray(value.item_en_mention).join(" ");
  const structured = joinTextParts([value.text_before, mentions, value.text_after]);
  if (structured) return structured;

  return joinTextParts([value.traduction, value.text, mentions]);
}

function getMixedTextParts(source: unknown, fallbackItemTraite?: unknown): ItemPart[] {
  if (!isRecord(source)) return [];

  const itemTraiteValues = toStringArray(source.item_traite ?? fallbackItemTraite);
  const textBefore = typeof source.text_before === "string" ? source.text_before : "";
  const textAfter = typeof source.text_after === "string" ? source.text_after : "";
  const text = typeof source.text === "string" ? source.text : "";

  const parts: ItemPart[] = [];
  if (textBefore.trim()) {
    parts.push({ text: textBefore, isItemTraite: false });
  }

  itemTraiteValues.forEach((item) => {
    parts.push({ text: item, isItemTraite: true });
  });

  if (textAfter.trim()) {
    parts.push({ text: textAfter, isItemTraite: false });
  }

  if (!parts.length && text.trim()) {
    parts.push({ text, isItemTraite: false });
  }

  return parts;
}

/* POS mapping pour filtres */
const FILTERS = [
  { key: "all", label: "Toutes catégories" },
  { key: "cult", label: "Cult." },
  { key: "hist", label: "Hist." },
  { key: "leg",  label: "Lég." },
  { key: "loc",  label: "Loc." },
  { key: "prov", label: "Prov." },
  { key: "syn",  label: "Syn." },
  { key: "strd", label: "Strd." },
  { key: "var",  label: "Var." },
];

function matchesFilter(hit: SearchHit, active: string) {
  if (active === "all") return true;
  const a: Article = hit?._source || {} as Article;
  const encyclopedique =
    a?.composant_de_traitement?.[0] && !Array.isArray(a.composant_de_traitement[0].composant_encyclopedique)
      ? a.composant_de_traitement[0].composant_encyclopedique?.introducteur_encyclopedique || ""
      : "";
  const full = (a?.full_text || "").toLowerCase();

  switch (active) {
    case "cult": return /cult\./i.test(encyclopedique) || /cult\./i.test(full);
    case "hist": return /hist/i.test(encyclopedique) || /hist/.test(full);
    case "leg":  return /lég\./i.test(encyclopedique) || /leg\./i.test(encyclopedique) || /lég\./i.test(full);
    case "loc":  return /loc\./i.test(full);
    case "prov": return /prov\./i.test(full);
    case "syn":  return /syn\./i.test(full);
    case "strd": return /strd\./i.test(full);
    case "var":  return /var\./i.test(full) || /variante/i.test(full);
    default:    return true;
  }
}

function capitalize(s = "") {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/* ===== Carte résultat ===== */
function ResultItem({ hit, query }: { hit: SearchHit; query: string }) {
  const a: Article = hit?._source || {} as Article;

  console.log("Article source:", a);

  
  // Handle Lemma construction with safety checks
  const lemmaSegments =
    a.composant_d_adressage?.item_en_adresse
      ?.map((item) => {
        const augmentRaw = (item.augment || "").trim();
        const radicalRaw = (item.radical || "").trim();
        const combined = `${augmentRaw}${radicalRaw}`.trim();
        if (!combined) return null;

        if (augmentRaw) {
          return {
            augment: capitalize(augmentRaw),
            radical: radicalRaw,
          };
        }

        return {
          augment: "",
          radical: capitalize(radicalRaw),
        };
      })
      .filter(Boolean) || [];

  const lemma =
    lemmaSegments.length > 0
      ? lemmaSegments.map((segment) => `${segment?.augment || ""}${segment?.radical || ""}`).join(" | ")
      : "(entrée)";

  // Handle composant_d_adressage and categorisation with safety checks
  const phonetique = a.composant_d_adressage?.transcription_phonetique || "";
  const categorie  = a.composant_d_adressage?.categorisation?.categorie || "";
  const classificateur  = a.composant_d_adressage?.categorisation?.classificateur || "";
  const nature  = a.composant_d_adressage?.categorisation?.nature || "";
  const constructon_morphologique  = a.composant_d_adressage?.categorisation?.construction_morphologique || "";
  const nombre = a.composant_d_adressage?.categorisation?.nombre || "";
  const rection = a.composant_d_adressage?.categorisation?.rection || "";
  const terminaison = a.composant_d_adressage?.categorisation?.terminaison || "";
  const renvoiForme = [
    a.composant_d_adressage?.composant_de_renvoi_vers_forme?.symbole_introducteur_de_renvoi,
    a.composant_d_adressage?.composant_de_renvoi_vers_forme?.forme_de_renvoi,
    a.composant_de_traitement?.[0]?.composant_de_renvoi?.symbole_introducteur_de_renvoi,
    a.composant_de_traitement?.[0]?.composant_de_renvoi?.item_de_renvoi?.join(", "),
  ]
    .filter(Boolean)
    .join(" ");
  const etymologie = [
    a.composant_d_adressage?.etymologie?.langue,
    a.composant_d_adressage?.etymologie?.etymon,
  ]
    .filter(Boolean)
    .join(", ");

  const actance = [
    a.composant_d_adressage?.actance?.type_d_actance,
    a.composant_d_adressage?.actance?.symbole,
    a.composant_d_adressage?.actance?.forme_source,
  ]
    .filter(Boolean)
    .join(" ");

  // Handle Unite de traitement with safety checks
  const uniteDeTraitementArray = Array.isArray(a.composant_de_traitement?.[0]?.unite_de_traitement)
    ? a.composant_de_traitement?.[0]?.unite_de_traitement
    : [a.composant_de_traitement?.[0]?.unite_de_traitement];

  const handleUniteDeTraitement = (uniteArray: typeof uniteDeTraitementArray) => {
    const result: RenderedUnite[] = [];
    
    uniteArray.forEach((ut) => {
      if (!ut) return;
      
      console.log("Processing UniteDeTraitement:", ut);
      // Traduction principale
      if (ut.traduction_en_francais) {
        result.push({
          type: "traduction",
          intro: ut.indicateur,
          content: formatTraduction(ut.traduction_en_francais),
        });
      }
      
      // Contextualisation
      if (ut.composant_de_contextualisation) {
        const ctx = ut.composant_de_contextualisation;
        if (ctx.contextualisation || ctx.item_traite || ctx.text_before || ctx.text_after || ctx.text) {
          const isContextualisationList = Array.isArray(ctx.contextualisation);
          const contextualisationValues = isContextualisationList
            ? (ctx.contextualisation as unknown[])
                .filter(Boolean)
                .map((v: unknown) => String(v))
            : typeof ctx.contextualisation === "string"
              ? [ctx.contextualisation]
              : [];

          const itemTraiteValues = toStringArray(ctx.item_traite);

          const nestedContextualisationParts = getMixedTextParts(ctx.contextualisation, ctx.item_traite);
          const hasDirectStructuredText = Boolean(ctx.text_before || ctx.text_after || ctx.text);
          const directContextualisationParts = hasDirectStructuredText
            ? getMixedTextParts(ctx, ctx.item_traite)
            : [];

          const listFallbackParts = isContextualisationList
            ? (() => {
                const parts: ItemPart[] = [];
                const maxLen = Math.max(contextualisationValues.length, itemTraiteValues.length);
                for (let index = 0; index < maxLen; index += 1) {
                  if (contextualisationValues[index]) {
                    parts.push({ text: contextualisationValues[index], isItemTraite: false });
                  }
                  if (itemTraiteValues[index]) {
                    parts.push({ text: itemTraiteValues[index], isItemTraite: true });
                  }
                }
                return parts;
              })()
            : [];

          const contextualisationParts = nestedContextualisationParts.length
            ? nestedContextualisationParts
            : directContextualisationParts.length
              ? directContextualisationParts
              : listFallbackParts.length
                ? listFallbackParts
              : [
                  ...itemTraiteValues.map((text) => ({ text, isItemTraite: true })),
                  ...contextualisationValues.map((text: string) => ({ text, isItemTraite: false })),
                ];

          result.push({
            type: "Contextualisation",
            intro: ctx.symbole_introducteur_de_contextualisation || "",
            content: contextualisationValues.join(" ") || contextualisationParts.map((part) => part.text).join(" "),
            contextualisationParts,
            reference: ctx.reference_de_contextualisation,
            traduction: formatTraduction(ctx.traduction_en_francais),
          });
        }
      }
      
      // Locution
      if (ut.composant_de_locution) {
        const loc = ut.composant_de_locution;
        if (loc.locution) {
          result.push({
            type: "Locution",
            intro: loc.introducteur_de_locution || "",
            content: loc.locution,
            traduction: formatTraduction(loc.traduction_en_francais),
          });
        }
      }
      
      // Contraire
      if (ut.composant_de_contraire) {
        const contr = ut.composant_de_contraire;
        if (contr.contraire) {
          result.push({
            type: "Contraire",
            intro: contr.symbole_introducteur_de_contraire || "",
            content: contr.contraire,
            traduction: formatTraduction(contr.traduction_en_francais),
          });
        }
      }
      
      // Dérivation
      if (ut.composant_de_derivation) {
        const deriv = ut.composant_de_derivation;
        if (deriv.derivation) {
          result.push({
            type: "Derivation",
            intro: deriv.symbole_introducteur_de_derivation || "",
            content: deriv.derivation,
            traduction: formatTraduction(deriv.traduction_en_francais),
          });
        }
      }
      
      // Synonymie
      if (ut.composant_de_synonymie?.synonyme?.length) {
        result.push({
          type: "Synonyme",
          intro: ut.composant_de_synonymie.introducteur_de_synonymie || "",
          content: ut.composant_de_synonymie.synonyme.join(", "),
        });
      }
      
      // Variante
      if (ut.composant_de_variante?.item_en_variante?.length) {
        result.push({
          type: "Variante",
          intro: ut.composant_de_variante.introducteur_de_variante || "",
          content: ut.composant_de_variante.item_en_variante.join(", "),
        });
      }
      
      // Proverbe
      if (ut.composant_de_proverbe) {
        const prov = ut.composant_de_proverbe;
        const proverbeParts = getMixedTextParts(prov.proverbe);
        const proverbeContent = proverbeParts.length
          ? proverbeParts.map((part) => part.text).join(" ")
          : typeof prov.proverbe === "string"
            ? prov.proverbe
            : prov.proverbe?.item_traite?.join(", ") || "";
        result.push({
          type: "Proverbe",
          intro: prov.introducteur_de_proverbes || "",
          content: proverbeContent,
          contextualisationParts: proverbeParts,
          reference: prov.reference_de_contextualisation,
          traduction: formatTraduction(prov.traduction_en_francais),
          explication: prov.explication,
        });
      }
      
      // Renvoi
      if (ut.composant_de_renvoi?.item_de_renvoi?.length) {
        result.push({
          type: "Renvoi",
          intro: ut.composant_de_renvoi.symbole_introducteur_de_renvoi || "",
          content: ut.composant_de_renvoi.item_de_renvoi.join(", "),
        });
      }
    });
    
    return result;
  };

  const uniteContent = handleUniteDeTraitement(uniteDeTraitementArray);

  const expandIntro = (intro = "") => {
    const normalized = intro.trim();
    if (/^Fam\.$/i.test(normalized)) return "Familier";
    if (/^Néol\.$/i.test(normalized) || /^Neol\.$/i.test(normalized)) return "Néologisme";
    return normalized;
  };
  
  //Handle complexe cases of UniteDeTraitement
  
  const encyclopediqueContent = handleCompEncyclopedique(a);



  return (
    <article className="nyA-item" aria-label={lemma}>
      <header className="nyA-lemma">
        <h3 className="nyA-lemmaWord">
          {lemmaSegments.length > 0 ? (
            lemmaSegments.map((segment, index) => (
              <React.Fragment key={`${segment?.augment || ""}-${segment?.radical || ""}-${index}`}>
                {segment?.augment && (
                  <span className="nyA-lemmaAug">{segment.augment}</span>
                )}
                {segment?.radical && (
                  <span className="nyA-lemmaRadical">{segment.radical}</span>
                )}
                {index < lemmaSegments.length - 1 && <span> | </span>}
              </React.Fragment>
            ))
          ) : (
            <span>{lemma}</span>
          )}
        </h3>
        {phonetique && <span className="nyA-lemmaPhon">{phonetique}</span>}
      </header>

      {(categorie || classificateur) && (
        <div className="nyA-chips">
          {categorie && <span className="nyA-chip">{categorie}</span>}
          {classificateur && <span>cl. {classificateur}</span>}
          {nature && <span> {nature}</span>}
          {constructon_morphologique && <span> {constructon_morphologique}</span>}
          {nombre && <span> {nombre}</span>}
          {rection && <span> {rection}</span>}
          {terminaison && <span> {terminaison}</span>}
          {renvoiForme && <span> {renvoiForme}</span>}
          {etymologie && <><span className="nyA-chip"> Étymologie </span> <span> ({etymologie}) </span> </>}
          {actance && <><span className="nyA-chip"> Actance </span> <span> ({actance}) </span> </>}
        </div>
      )}

      {uniteContent.length > 0 && (
        <div className="nyA-defGroup">
          {uniteContent.map((item, index) => (
            <div key={`${item.type}-${index}`} className={`nyA-defRow nyA-defRow--${item.type}`}>
              {item.type === "traduction" ? (
                <div>
                  {item.intro && <span className="nyA-badge">{expandIntro(item.intro)}</span>}
                  <p
                    className="nyA-def"
                    dangerouslySetInnerHTML={{ __html: highlight(item.content || "", query) }}
                  />
                </div>
              ) : item.type === "Contextualisation" ? (
                <div>
                  <span className="nyA-badge">{item.type || item.intro}</span>
                  <span>{item.reference ? ` ${item.reference}` : ""}</span>
                  <p className="nyA-note">
                    {(item.contextualisationParts?.length ? item.contextualisationParts : [{ text: item.content || "", isItemTraite: false }]).map((part: { text: string; isItemTraite: boolean }, partIndex: number) => (
                      <React.Fragment key={`${item.type}-${index}-part-${partIndex}`}>
                        {partIndex > 0 && " "}
                        <span
                          className={part.isItemTraite ? "nyA-itemTraite" : undefined}
                          dangerouslySetInnerHTML={{ __html: highlight(part.text || "", query) }}
                        />
                      </React.Fragment>
                    ))}
                  </p>
                </div>
              ) : item.type === "Proverbe" && item.contextualisationParts?.length ? (
                <div>
                  <span className="nyA-badge">{item.type || item.intro}</span>
                  <span>{item.reference ? ` ${item.reference}` : ""}</span>
                  <p className="nyA-note">
                    {item.contextualisationParts.map((part: { text: string; isItemTraite: boolean }, partIndex: number) => (
                      <React.Fragment key={`${item.type}-${index}-part-${partIndex}`}>
                        {partIndex > 0 && " "}
                        <span
                          className={part.isItemTraite ? "nyA-itemTraite" : undefined}
                          dangerouslySetInnerHTML={{ __html: highlight(part.text || "", query) }}
                        />
                      </React.Fragment>
                    ))}
                  </p>
                </div>
              ) : (
                <div>
                  <span className="nyA-badge">{(item.type || item.intro) }</span>
                  <p
                    className={`nyA-note ${item.type === "Locution" ? "nyA-locutionText" : ""}`.trim()}
                    dangerouslySetInnerHTML={{ __html: highlight(item.content || "", query) }}
                  />
                </div>
              )}

              {item.traduction && item.type !== "traduction" && (
                <p
                  className="nyA-def nyA-def--traduction"
                  dangerouslySetInnerHTML={{ __html: highlight(item.traduction, query) }}
                />
              )}

              {item.explication && (
                <p
                  className="nyA-note"
                  dangerouslySetInnerHTML={{ __html: highlight(item.explication, query) }}
                />
              )}
            </div>
          ))}
        </div>
      )}

      {encyclopediqueContent.length > 0 && (
        <>
          {encyclopediqueContent.map((item, index) => (
            <div key={index} className="nyA-notice">
              <div className="nyA-notice-item">
                <span className="nyA-badge">{item.badge}</span>
                <p
                  className="nyA-note"
                  dangerouslySetInnerHTML={{
                    __html: highlight(item.text, query)
                  }}
                />
              </div>
            </div>
          ))}
        </>
      )}
    </article>
  );
              
}

function handleCompEncyclopedique(a: Article) {
  const encyclopedique = a.composant_de_traitement?.[0]?.composant_encyclopedique;

  const encyclopediqueContent = [];
  const formatBadge = (value = "") => {
    const cleaned = value.replace(/^\s*[\u2660\s]*/u, "").trim();
    if (/^Hist\.$/i.test(cleaned)) return "Histoire";
    if (/^Lég\.$/i.test(cleaned) || /^Leg\.$/i.test(cleaned)) return "Légende";
    return cleaned || "Notice";
  };

  if (Array.isArray(encyclopedique)) {
    encyclopedique.forEach((comp) => {
      if (comp.introducteur_encyclopedique || comp.texte_encyclopedique) {
        encyclopediqueContent.push({
          badge: formatBadge(comp.introducteur_encyclopedique),
          text: comp.texte_encyclopedique || "",
        });
      }
    });
  } else if (encyclopedique?.introducteur_encyclopedique || encyclopedique?.texte_encyclopedique) {
    encyclopediqueContent.push({
      badge: formatBadge(encyclopedique.introducteur_encyclopedique),
      text: encyclopedique.texte_encyclopedique || "",
    });
  }
  return encyclopediqueContent;
}

/* ===== Landing zéro-état (hero compact + filtres + thématiques) ===== */
function ZeroStateCompact({ onPickSuggestion, activeFilter, setActiveFilter }: {
  onPickSuggestion: (word: string) => void;
  activeFilter: string;
  setActiveFilter: React.Dispatch<React.SetStateAction<string>>;
}) {
  return (
    <div className="nyZ">
      {/* Filtres compacts juste sous la recherche */}
      <section className="nyZ-searchline">
        <div className="nyZ-wrap nyZ-filters">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              className={`nyZ-filter ${activeFilter === f.key ? "is-active" : ""}`}
              onClick={() => setActiveFilter(f.key)}
              type="button"
            >
              {f.label}
            </button>
          ))}
        </div>
      </section>

      {/* Suggestions + Sections (sans hero supplémentaire) */}
      <section className="nyZ-wrap nyZ-grid">
        <div className="nyZ-card">
          <h3>Suggestions rapides</h3>
          <div className="nyZ-body nyZ-tags">
            {["umugabo", "abantu", "Mutara", "ubwoko", "kinyarwanda", "amateka"].map((w) => (
              <button key={w} className="nyZ-tag" onClick={() => onPickSuggestion(w)} type="button">
                {w}
              </button>
            ))}
          </div>
        </div>

        <div className="nyZ-card">
          <h3>Explorer les sections</h3>
          <div className="nyZ-body nyZ-topics">
            <a className="nyZ-topic" href="/orthographe"><span>Orthographe</span><span>↗</span></a>
            <a className="nyZ-topic" href="/elements-de-grammaire"><span>Éléments de grammaire</span><span>↗</span></a>
            <a className="nyZ-topic" href="/signes-abreviations"><span>Signes & abréviations</span><span>↗</span></a>
            <a className="nyZ-topic" href="/breve-histoire"><span>Brève histoire</span><span>↗</span></a>
            <a className="nyZ-topic" href="/dictionnaire"><span>Dictionnaire</span><span>↗</span></a>
          </div>
        </div>
      </section>
    </div>
  );
}


/* ===== Page ===== */
export default function Recherche() {
  const [query, setQuery] = useState("");
  const [executedQuery, setExecutedQuery] = useState("");
  const [submittedShortQuery, setSubmittedShortQuery] = useState("");
  const [activeFilter, setActiveFilter] = useState("all");
  const { data: results, error, isLoading } = useSearchData(executedQuery);

  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length >= 3) {
      setExecutedQuery(trimmed);
      setSubmittedShortQuery("");
    }
  }, [query]);

  useEffect(() => {
    // reset filtre au changement de requête
    setActiveFilter("all");
  }, [query]);

  const handleSearch = (e?: React.FormEvent<HTMLFormElement>) => {
    e?.preventDefault?.();
    const trimmed = query.trim();
    if (!trimmed) {
      setExecutedQuery("");
      setSubmittedShortQuery("");
      return;
    }

    if (trimmed.length < 3) {
      setExecutedQuery(trimmed);
      setSubmittedShortQuery(trimmed);
    }
  };

  const shouldShowZeroState =
    query.trim() === "" ||
    (query.trim().length < 3 && submittedShortQuery !== query.trim());

  // Filtrage client des résultats selon POS/Hist.
  const filtered = useMemo(
    () => results.filter((h) => matchesFilter(h, activeFilter)),
    [results, activeFilter]
  );

  const onPickSuggestion = (w: string) => {
    setQuery(w);
  };

  return (
    <main className="nyA-page is-ready">
      {/* HERO éditorial (Variante A existante) */}
      <section className="nyA-hero">
        <div className="nyA-wrap nyA-heroGrid">
          <div>
            <div className="nyA-kicker">Dictionnaire Kinyarwanda–Français</div>
            <h1 className="nyA-h1">Trouvez le mot juste, explorez le sens, la langue et l’histoire.</h1>
            <p className="nyA-sub">
              Possibilité de mettre du texte
            </p>
          </div>
          <figure className="nyA-heroIllus">
          </figure>
        </div>
      </section>

      {/* BARRE DE RECHERCHE */}
      <section className="nyA-search nyA-wrap">
        <form className="nyA-searchForm" onSubmit={handleSearch} role="search">
          <span className="nyA-searchIcon" aria-hidden />
          <input
            type="text"
            className="nyA-input"
            placeholder="Rechercher un mot"
            aria-label="Rechercher un mot"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <button type="submit" className="nyA-btn nyA-btnPrimary">Rechercher</button>
        </form>
      </section>

      {/* ZERO-STATE (si pas de requête) */}
{shouldShowZeroState ? (
  <ZeroStateCompact
    onPickSuggestion={onPickSuggestion}
    activeFilter={activeFilter}
    setActiveFilter={setActiveFilter}
  />
) : (
        /* CONTENT (résultats) */
        <section className="nyA-content nyA-wrap">
          <div className="nyA-resultsCol">
            {/* Filtres réutilisés au-dessus des résultats */}
            <div className="nyZ-filters nyZ-filters--inline">
              {FILTERS.map((f) => (
                <button
                  key={f.key}
                  className={`nyZ-filter ${activeFilter === f.key ? "is-active" : ""}`}
                  onClick={() => setActiveFilter(f.key)}
                  type="button"
                >
                  {f.label}
                </button>
              ))}
            </div>

            {error && (
              <div className="nyA-state nyA-error">
                <strong>Une erreur est survenue</strong>
                <div className="nyA-stateSub">{error}</div>
              </div>
            )}

            {isLoading && (
              <ul className="nyA-list">
                {Array.from({ length: 6 }).map((_, i) => (
                  <li key={i} className="nyA-skel" style={{ animationDelay: `${i * 40}ms` }}>
                    <div className="nyA-sLine nyA-sTitle" />
                    <div className="nyA-sLine" />
                    <div className="nyA-sLine nyA-sShort" />
                  </li>
                ))}
              </ul>
            )}

            {!isLoading && filtered.length === 0 && query.trim() && !error && (
              <div className="nyA-state">
                <strong>Aucun résultat pour « {query} »</strong>
                <div className="nyA-stateSub">
                  Essayez le radical, une orthographe proche, ou un autre terme.
                </div>
              </div>
            )}

            {!isLoading && filtered.length > 0 && (
              <>
                <div className="nyA-meta">
                  {filtered.length} résultat{filtered.length > 1 ? "s" : ""} pour « {query} »
                </div>
                <ul className="nyA-list">
                  {filtered.map((hit) => (
                    <li key={hit._id} className="nyA-listItem">
                      <ResultItem hit={hit} query={query} />
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>

          {/* Rail éditorial (inchangé) */}
          <aside className="nyA-rail">
            <div className="nyA-card">
              <h4>Mot du jour</h4>
              <div className="nyA-cardBody">
                <div className="nyA-lemma" style={{ marginBottom: 6 }}>
                  <h3 className="nyA-lemmaWord" style={{ fontSize: 22 }}>Ababaga</h3>
                  <span className="nyA-lemmaPhon">/ababáaga/</span>
                </div>
                <div className="nyA-chips" style={{ marginBottom: 6 }}>
                  <span className="nyA-chip">n.</span><span>cl. 1</span>
                </div>
                <p className="nyA-def">« Les découpeurs ». Milice créée sous le règne de Mutara II Rwogera (1802–1853)…</p>
              </div>
            </div>

            <div className="nyA-card">
              <h4>Recherches tendances</h4>
              <div className="nyA-cardBody nyA-trend">
                {["umugabo", "abantu", "Mutara", "ubwoko"].map((w) => (
                  <button
                    key={w}
                    className="nyA-linkBtn"
                    onClick={() => {
                      setQuery(w);
                    }}
                  >
                    <span>{w}</span><span>↗</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="nyA-card">
              <h4>Illustration</h4>
              <div className="nyA-cardBody">
                <div className="nyA-illus">
                  <img
                    src="https://images.unsplash.com/photo-1543165796-5426273eaab3?q=80&w=1200&auto=format&fit=crop"
                    alt="Illustration culturelle"
                    loading="lazy"
                  />
                </div>
              </div>
            </div>
          </aside>
        </section>
      )}
    </main>
  );
}
